import React from 'react';
import {
  FiCheckCircle,
  FiTrendingUp,
  FiAlertCircle,
} from 'react-icons/fi';

import type {
  AgentInsight,
  InsightCategory,
} from '../../services/agentAnalyticsApi';

interface InsightCardProps {
  variant: InsightCategory;
  title: string;
  items: AgentInsight[];
  /** Mostra mensagem "nenhum item" quando o array de items vem vazio */
  emptyMessage: string;
}

/**
 * Card narrativo reutilizável que renderiza uma das 3 categorias de
 * insights gerados pelo LLM: "faz bem", "pode melhorar" ou "detectados".
 *
 * Cada variant tem ícone + accent color próprios (verde, amarelo, azul).
 * Quando a lista de items está vazia, mostra `emptyMessage` ao invés de
 * esconder o card — assim a UI fica estável entre estados.
 */
const VARIANT_CONFIG: Record<
  InsightCategory,
  { accent: string; icon: React.ReactNode; label: string }
> = {
  positive: {
    accent: '#10b981',
    icon: <FiCheckCircle className="h-5 w-5" />,
    label: 'Pontos fortes',
  },
  improvement: {
    accent: '#f59e0b',
    icon: <FiTrendingUp className="h-5 w-5" />,
    label: 'Oportunidades',
  },
  insight: {
    accent: '#8b5cf6',
    icon: <FiAlertCircle className="h-5 w-5" />,
    label: 'Observações',
  },
};

export const InsightCard: React.FC<InsightCardProps> = ({
  variant,
  title,
  items,
  emptyMessage,
}) => {
  const config = VARIANT_CONFIG[variant];

  return (
    <div
      className="rounded-lg border p-5 h-full flex flex-col"
      style={{
        backgroundColor: 'var(--theme-card-background)',
        borderColor: 'var(--theme-card-border)',
        borderTopWidth: 3,
        borderTopColor: config.accent,
      }}
    >
      <div className="flex items-center gap-2 mb-4">
        <div style={{ color: config.accent }}>{config.icon}</div>
        <h3
          className="text-base font-semibold"
          style={{ color: 'var(--theme-card-text)' }}
        >
          {title}
        </h3>
      </div>

      {items.length === 0 ? (
        <p
          className="text-sm flex-1"
          style={{ color: 'var(--theme-muted-text)' }}
        >
          {emptyMessage}
        </p>
      ) : (
        <ul className="space-y-3 flex-1">
          {items.map((item, idx) => (
            <li key={`${variant}-${idx}`}>
              <div
                className="text-sm font-medium mb-1"
                style={{ color: 'var(--theme-card-text)' }}
              >
                {item.title}
              </div>
              <div
                className="text-sm leading-relaxed"
                style={{ color: 'var(--theme-muted-text)' }}
              >
                {item.description}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
